// CareLogix™ ORACLE — Access Login Options screen (Phase 23).
//
// Mounts at `/access`. Mobile-first, dark green/gold premium theme, additive.
// All button states are functional — no dead tabs. Apple/Microsoft/Phone/Email
// providers route through `/api/auth/oauth/coming-soon` to keep the flow alive
// until real OAuth credentials are wired.
//
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
// THIS BREAKS THE EMERGENT GOOGLE AUTH FLOW.
import React, { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

const COMPLIANCE_NOTES = [
  "Educational and research only — not financial advice",
  "No guaranteed profit. Past performance ≠ future results",
  "Sports betting intelligence is 21+ where legal",
  "Human-in-the-loop required for high-risk outputs",
];

const COMING_SOON_PROVIDERS = [
  { id: "apple",     label: "Continue with Apple",     glyph: "" },
  { id: "microsoft", label: "Continue with Microsoft", glyph: "▮" },
  { id: "phone",     label: "Continue with Phone",     glyph: "📱" },
];

export default function AccessLogin() {
  const navigate = useNavigate();
  const {
    user, isAuthenticated, isGuest, signIn, signUp, signInGoogle, startGuest,
    signInComingSoon, isRestrictedWebview,
  } = useAuth();

  const [mode, setMode] = useState("login");     // "login" | "register" | "email"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");
  const [comingSoon, setComingSoon] = useState(null);
  const [showWebviewWarn, setShowWebviewWarn] = useState(isRestrictedWebview);

  const goHome = useCallback(() => {
    setTimeout(() => navigate("/"), 200);
  }, [navigate]);

  const goToOracleDashboard = useCallback(() => {
    navigate("/oracle/dashboard");
  }, [navigate]);

  // Phase 39 — when an ORACLE member session is already present we must NOT
  // render the full sign-in funnel as the primary action. Operators visiting
  // `/access` while authenticated should see "Already signed in" + a clear
  // Continue-to-ORACLE CTA, with Switch Account / Sign Out as escape hatches.
  const alreadySignedIn = isAuthenticated || isGuest;

  const handleGoogle = useCallback(() => {
    if (isRestrictedWebview) {
      setShowWebviewWarn(true);
      return;
    }
    setErr("");
    setInfo("Redirecting to Google sign-in…");
    // Synchronous redirect — per Emergent Auth playbook.
    signInGoogle();
  }, [isRestrictedWebview, signInGoogle]);

  const handleComingSoon = useCallback(async (provider) => {
    setErr("");
    setInfo("");
    setComingSoon(null);
    try {
      const res = await signInComingSoon(provider);
      setComingSoon({ provider, message: res?.message || `${provider} sign-in is coming soon.` });
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Provider currently unavailable.");
    }
  }, [signInComingSoon]);

  const handleGuest = useCallback(async () => {
    setBusy(true); setErr(""); setInfo("");
    try {
      await startGuest();
      setInfo("Guest session started. Redirecting to ORACLE…");
      goHome();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Could not start guest session.");
    } finally { setBusy(false); }
  }, [startGuest, goHome]);

  const handleLogin = useCallback(async (e) => {
    e?.preventDefault?.();
    setBusy(true); setErr(""); setInfo("");
    try {
      const payload = await signIn(email.trim(), password);
      setInfo(`Welcome back, ${payload?.user?.name || payload?.user?.email}.`);
      goHome();
    } catch (e2) {
      const detail = e2?.response?.data?.detail;
      setErr(typeof detail === "string" ? detail : (e2?.message || "Login failed."));
    } finally { setBusy(false); }
  }, [signIn, email, password, goHome]);

  const handleRegister = useCallback(async (e) => {
    e?.preventDefault?.();
    setBusy(true); setErr(""); setInfo("");
    try {
      const payload = await signUp(email.trim(), password, name.trim());
      setInfo(`Account created. Welcome, ${payload?.user?.name || payload?.user?.email}.`);
      goHome();
    } catch (e2) {
      const detail = e2?.response?.data?.detail;
      setErr(typeof detail === "string" ? detail : (e2?.message || "Registration failed."));
    } finally { setBusy(false); }
  }, [signUp, email, password, name, goHome]);

  return (
    <main className="cl-access" data-testid="access-login-page">
      {/* Restricted webview banner */}
      {showWebviewWarn && (
        <div className="cl-access__warn" role="alert" data-testid="access-webview-warning">
          <strong>Open in Browser:</strong> Google Sign-In requires a regular browser.
          Tap <em>Share → Open in Safari / Chrome</em> if you are inside Instagram, Facebook,
          TikTok, or another in-app browser.
          <button
            type="button"
            className="cl-access__warn-close"
            onClick={() => setShowWebviewWarn(false)}
            aria-label="Dismiss warning"
            data-testid="access-webview-warning-close"
          >×</button>
        </div>
      )}

      <header className="cl-access__hero">
        <div className="cl-access__logo" aria-hidden="true">
          <span className="cl-access__logo-glyph">🜂</span>
        </div>
        <h1 className="cl-access__title">Access CareLogix™ ORACLE</h1>
        <p className="cl-access__tagline" data-testid="access-tagline">Global Market Intelligence Operating System</p>
        <p className="cl-access__sub">
          AI-powered intelligence across <b>stocks</b>, <b>crypto</b>, <b>forex / OANDA</b>,{" "}
          <b>options</b>, <b>commodities</b>, <b>sports intelligence</b>, <b>healthcare investments</b>,{" "}
          <b>biotech / FDA catalysts</b>, <b>startups</b>, <b>grants</b>, <b>Africa markets</b>, and{" "}
          <b>global macro signals</b>.
        </p>
        <p className="cl-access__phi" data-testid="access-phi-note">
          No PHI collection &middot; Audit-ready &middot; Not financial advice
        </p>
      </header>

      {(isAuthenticated || isGuest) && (
        <section className="cl-access__signed-in" data-testid="access-signed-in">
          <p>
            Already signed in as <strong>{user?.name || user?.email || "Guest"}</strong>
            {user?.tier ? <> · <code>{user.tier}</code></> : null}.
          </p>
          <div className="cl-access__row">
            <button
              type="button"
              className="cl-access__btn cl-access__btn--primary"
              onClick={goToOracleDashboard}
              data-testid="access-continue-to-oracle"
            >
              Continue to ORACLE →
            </button>
            <button
              type="button"
              className="cl-access__btn cl-access__btn--ghost"
              onClick={async () => {
                try {
                  // Clear the session locally so the provider list re-appears
                  // and the user can pick a different account.
                  const { authStorage: _as } = await import("@/api/authClient");
                  _as.clear();
                } catch { /* ignore */ }
                window.location.reload();
              }}
              data-testid="access-switch-account"
            >
              Switch Account
            </button>
            <button
              type="button"
              className="cl-access__btn cl-access__btn--ghost"
              onClick={async () => {
                try {
                  const { authStorage: _as } = await import("@/api/authClient");
                  _as.clear();
                } catch { /* ignore */ }
                navigate("/");
              }}
              data-testid="access-logout"
            >
              Log Out
            </button>
          </div>
        </section>
      )}

      {!alreadySignedIn && (
      <section className="cl-access__providers" aria-label="Sign-in providers">
        <button
          type="button"
          className="cl-access__btn cl-access__btn--google"
          onClick={handleGoogle}
          disabled={busy}
          data-testid="access-btn-google"
        >
          <span className="cl-access__glyph cl-access__glyph--google" aria-hidden="true">G</span>
          Continue with Google
        </button>

        {COMING_SOON_PROVIDERS.map((p) => (
          <button
            key={p.id}
            type="button"
            className="cl-access__btn cl-access__btn--ghost"
            onClick={() => handleComingSoon(p.id)}
            disabled={busy}
            data-testid={`access-btn-${p.id}`}
          >
            <span className="cl-access__glyph" aria-hidden="true">{p.glyph}</span>
            {p.label}
          </button>
        ))}

        <button
          type="button"
          className="cl-access__btn cl-access__btn--ghost"
          onClick={() => { setMode("login"); setErr(""); setInfo(""); }}
          data-testid="access-btn-email"
        >
          <span className="cl-access__glyph" aria-hidden="true">✉</span>
          Continue with Email
        </button>

        {comingSoon && (
          <p className="cl-access__hint" data-testid="access-coming-soon">
            <strong>{comingSoon.provider.charAt(0).toUpperCase() + comingSoon.provider.slice(1)}:</strong> {comingSoon.message}
          </p>
        )}
      </section>
      )}

      {!alreadySignedIn && (
      <section className="cl-access__email" aria-label="Email and password">
        <div className="cl-access__tabs" role="tablist" aria-label="Email form mode">
          <button
            type="button"
            role="tab"
            aria-selected={mode === "login"}
            className={`cl-access__tab ${mode === "login" ? "is-active" : ""}`}
            onClick={() => { setMode("login"); setErr(""); setInfo(""); }}
            data-testid="access-tab-login"
          >Log In</button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === "register"}
            className={`cl-access__tab ${mode === "register" ? "is-active" : ""}`}
            onClick={() => { setMode("register"); setErr(""); setInfo(""); }}
            data-testid="access-tab-register"
          >Create Account</button>
        </div>

        <form
          className="cl-access__form"
          onSubmit={mode === "register" ? handleRegister : handleLogin}
          data-testid={`access-form-${mode}`}
        >
          {mode === "register" && (
            <label className="cl-access__field">
              <span>Full name (optional)</span>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="name"
                maxLength={120}
                placeholder="Ada Lovelace"
                data-testid="access-input-name"
              />
            </label>
          )}
          <label className="cl-access__field">
            <span>Email</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
              maxLength={320}
              placeholder="you@example.com"
              data-testid="access-input-email"
            />
          </label>
          <label className="cl-access__field">
            <span>Password</span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete={mode === "register" ? "new-password" : "current-password"}
              required
              minLength={mode === "register" ? 8 : 1}
              maxLength={128}
              placeholder={mode === "register" ? "At least 8 characters" : "Your password"}
              data-testid="access-input-password"
            />
          </label>

          {err && <p className="cl-access__err" role="alert" data-testid="access-error">{err}</p>}
          {err && /carelogix360\.com$/i.test(email.trim()) && (
            <div
              role="status"
              data-testid="access-operator-hint"
              style={{
                marginTop: 8,
                padding: "10px 12px",
                border: "1px solid rgba(245,200,66,0.45)",
                background: "rgba(245,200,66,0.08)",
                borderRadius: 10,
                color: "#fde68a",
                fontSize: 13,
                lineHeight: 1.45,
              }}
            >
              That looks like an AdminOne operator email. The CEO console lives on a separate
              authentication scope.{" "}
              <a
                href="/adminone/login"
                data-testid="access-operator-redirect"
                style={{ color: "#ffd966", fontWeight: 700, textDecoration: "underline" }}
              >
                Go to /adminone/login →
              </a>
            </div>
          )}
          {info && <p className="cl-access__info" data-testid="access-info">{info}</p>}

          <button
            type="submit"
            className="cl-access__btn cl-access__btn--gold"
            disabled={busy}
            data-testid={mode === "register" ? "access-btn-create" : "access-btn-login"}
          >
            {busy ? "Working…" : (mode === "register" ? "Create Account" : "Log In")}
          </button>
        </form>
      </section>
      )}

      {!alreadySignedIn && (
      <section className="cl-access__guest" aria-label="Guest access">
        <button
          type="button"
          className="cl-access__btn cl-access__btn--guest"
          onClick={handleGuest}
          disabled={busy}
          data-testid="access-btn-guest"
        >
          Continue as Guest
        </button>
        <p className="cl-access__hint">
          Guest access is limited to public ORACLE modes. LOGIX-2, Pro Trader, and paid features
          remain gated.
        </p>
      </section>
      )}

      <footer className="cl-access__legal" data-testid="access-legal">
        <ul>
          {COMPLIANCE_NOTES.map((n, i) => <li key={`cn-${i}`}>{n}</li>)}
        </ul>
        <p>
          AdminOne operators: use the dedicated portal at{" "}
          <a href="/adminone/login" data-testid="access-adminone-link">/adminone/login</a>.
        </p>
      </footer>
    </main>
  );
}
